/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import FileSystemPathFactory from '../file/system/file-system-path-factory';
import PromiseUtil from '../promise/promise-util';

/**
 * @import { Asset } from '../asset/asset'
 * @import { FileInfo } from '../asset/downloader/asset-downloader'
 * @import { IAssetDownloader } from '../asset/downloader/asset-downloader'
 * @import BasePathResolver from '../basepath/basepath'
 * @import { EventBus as IEventBus } from '../event-bus').EventBus
 * @import { ContentInfo } from '../content/info/content-info'
 * @import { ErrorEventListener } from '../event-listeners'
 * @import FileSystemPath from '../file/system/file-system-path'
 * @import Store from '../store/store'
 */

/**
 * @typedef {Pick<ContentInfo, 'media'>} IContentInfo
 * @typedef {IEventBus<EventTypes>} EventBus
 */

/**
 * Čistič místa na disku.
 * Snaží se zajistit dostatek volného místa pro nový obsah.
 * @implements {EventBus}
 */
class Cleaner {
  /**
   * @param {IAssetDownloader} assetDownloader
   * @param {BasePathResolver} basePathResolver Mapovač logických cesta na fyzické.
   * @param {Store} store Perzistentní read-only datový sklad s assety.
   */
  constructor(assetDownloader, basePathResolver, store) {
    this.assetDownloader = assetDownloader;
    this.basePathResolver = basePathResolver;
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    this.store = store;
    this.temporaryExtension = 'tmp';
  }

  /**
   * Pokusí se vyčistit místo na disku pro nový obsah. Tato metoda vždy uspěje.
   * Případné chyby jsou emitovány událostí 'error'.
   * @param {FileSystemPath} logicalPath Cesta ve které se provést čištění.
   * @param {IContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise<CleanerInfo>} Informace o vyčištěných datových modelech.
   * @fires ErrorEventListener TODO #555: Toto není ve skutečnosti event,
   * jak to dokumentovat správně?
   */
  async clean(logicalPath, contentInfo) {
    /** @type {CleanerInfo} */
    const cleanerInfo = {
      assets: [],
      files: []
    };

    try {
      // Seznam id assetů, které potřebuji pro aktuální contentInfo.
      const currentAssetIds = contentInfo.media
        .filter((medium) => !isNil(medium.asset))
        .map((medium) => /** @type {Asset} */ (medium.asset).id);

      const assets = await this.store.findAllAsset();
      // Seznam assetů, které nepatří do aktuálního contentInfo.
      const assetsFromPrevContentInfo = assets.filter(
        (asset) => !currentAssetIds.includes(asset.id)
      );
      if (assetsFromPrevContentInfo.length > 0) {
        // Bohužel asynchronní "požadavek" na zastavení aktuálně přehrávaného obsahu.
        // TODO #554: Refaktorovat na synchronní počkání na zastavení.
        this.emitter.emit('cleanerBeforeRemoveSomeAssets');
      }

      const [removedAssets, removedFiles] = await Promise.all([
        // Smažou se všechny soubory, které nemají vazbu na asset.
        PromiseUtil.allSerial(
          assetsFromPrevContentInfo.map(
            (asset) => () =>
              this.assetDownloader
                .destroy(asset.id)
                .then(() => asset)
                .catch((err) => {
                  this.emitter.emit('error', err);
                  return undefined;
                })
          )
        ),
        (async () => {
          try {
            const physicalPath = await this.basePathResolver.toPhysicalPath(
              logicalPath
            );
            // Vylistují se všechny soubory.
            const files = await this.assetDownloader.listFile(physicalPath);
            // Pole cest všech assetů z localStorage.
            const assetPaths = await Promise.all(
              assets
                .filter((a) => Boolean(a.path))
                .map(async (a) =>
                  (
                    await this.basePathResolver.toPhysicalPath(
                      FileSystemPathFactory.fromValue(a.path)
                    )
                  ).getValue()
                )
            );
            // TODO: Refaktorovat na efektivnější Set operace.
            const toRemoveFiles = files.filter(
              (f) => !assetPaths.includes(f.entry.getPath().getValue())
            );

            return /** @type {Promise<(FileInfo | undefined)[]>} */ (
              PromiseUtil.allSerial(
                toRemoveFiles.map(
                  (f) => () =>
                    f.entry
                      .remove({ recursive: true })
                      .then(() => f)
                      .catch((err) => {
                        this.emitter.emit('error', err);
                        return undefined;
                      })
                )
              )
            );
          } catch (err) {
            this.emitter.emit('error', /** @type {Error} */ (err));
            return [];
          }
        })()
      ]);

      const timeInMs = 1000;
      const sleepMs = (/** @type {number} */ ms) =>
        new Promise((resolve) => setTimeout(resolve, ms));
      // Po odstranění souborů chvíli trvá, než se tato informace projeví při
      // získávání volného místa.
      await sleepMs(timeInMs);

      cleanerInfo.assets = /** @type {Asset[]} */ (
        removedAssets.filter((asset) => !isNil(asset))
      );
      cleanerInfo.files = /** @type {FileInfo[]} */ (
        removedFiles.filter((f) => !isNil(f))
      );
    } catch (err) {
      this.emitter.emit('error', /** @type {Error} */ (err));
    }
    return cleanerInfo;
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }
}

/**
 * @typedef {Object} EventTypes
 * @property {CleanerBeforeRemoveSomeAssetsEventListener} cleanerBeforeRemoveSomeAssets
 * @property {ErrorEventListener} error
 */

/**
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @callback CleanerBeforeRemoveSomeAssetsEventListener
 * @returns {void}
 */

/**
 * Informace o vyčištěných datových modelech.
 * @typedef {Object} CleanerInfo
 * @property {Asset[]} assets Seznam assetů, které byly úspěšně odebrány.
 * @property {FileInfo[]} files Seznam souborů, které byly úspěšně odebrány.
 */

export default Cleaner;
